/**
 * Collection adapter
 * Converts Storefront API Collection to Analyzify format
 */

import type { StorefrontCollection } from '../types/storefront';
import type { AnalyzifyCollection } from '../types/products';
import { adaptProduct } from './product';
import { extractNumericId } from './ids';

/**
 * Adapt a Storefront API collection to Analyzify format
 * Defensively handles null/undefined/malformed collection data — reachable via
 * the public setPageContext entry (and this exported function), which must
 * never throw into the host app when passed partial API data.
 */
export function adaptCollection(
  collection: StorefrontCollection | null | undefined
): AnalyzifyCollection {
  // Adapt products — guard against missing products/edges (partial API responses)
  const productEdges = collection?.products?.edges;
  const products = Array.isArray(productEdges)
    ? productEdges.map((edge) => adaptProduct(edge?.node).product)
    : [];

  // Use totalCount from API if available, otherwise fall back to loaded products count
  const totalCount = collection?.products?.totalCount ?? products.length;

  return {
    id: extractNumericId(collection?.id),
    title: collection?.title || '',
    handle: collection?.handle || '',
    products: products,
    all_products_count: totalCount,
    products_count: totalCount,
  };
}

/**
 * Get collection data object for window.analyzify.getCollectionObj
 */
export function getCollectionDataObject(collection: StorefrontCollection): {
  collection: AnalyzifyCollection;
  collection_sku_list: string[];
  products: any[];
  id: number;
  title: string;
  handle: string;
} {
  const adaptedCollection = adaptCollection(collection);

  // Build SKU list - one SKU per product (first variant's SKU)
  // This matches the extension's behavior for collection pages
  const collection_sku_list: string[] = [];
  adaptedCollection.products.forEach((product) => {
    // Get SKU from first variant only
    const firstVariant = product.variants[0];
    if (firstVariant?.sku) {
      collection_sku_list.push(firstVariant.sku);
    }
  });

  return {
    collection: adaptedCollection,
    collection_sku_list,
    // Top-level fields required by CDN's gaViewItemList which destructures:
    // const { products, id, title, handle } = ga4Obj.getCollectionObj;
    products: adaptedCollection.products,
    id: adaptedCollection.id,
    title: adaptedCollection.title,
    handle: adaptedCollection.handle,
  };
}
